// Movatic WMS — Frontend | CesarFilho 932048016
// Arquivo: src/lib/api.ts
// -----------------------------------------------------------------------------
// Cliente Axios centralizado do Movatic WMS.
// - Única instância Axios para todo o app.
// - Base da API sempre termina em /api (sem /api/api).
// - Authorization: Bearer <access> automático.
// - Refresh inteligente com cache do endpoint e fila de requests 401.
// - Normalização de paths (remove /api duplicado, colapsa // e garante trailing slash).
// - Suporte a URLs absolutas (http/https) sem normalização indevida.
// - paramsSerializer compatível com DRF (repete chaves em arrays, sem [] )
// - Endpoints Workforce (equipamentos) com tipagens e fallbacks.
// -----------------------------------------------------------------------------

import axios from 'axios'
import type {
  AxiosError,
  AxiosResponse,
  InternalAxiosRequestConfig,
  AxiosRequestHeaders,
  AxiosRequestConfig,
} from 'axios'
import { useAuthStore } from '@/store/authStore'

// Pode ser injetado pelo bundler em tempo de build (opcional)
declare const __API_BASE__: string | undefined

// -----------------------------------------------------------------------------
// Base da API (sempre termina com **/api**, sem barra final extra)
// -----------------------------------------------------------------------------
function normalizeBaseUrl(v?: string): string {
  if (!v || !String(v).trim()) return '/api'
  let x = String(v).trim()
  x = x.replace(/\/+$/g, '') // remove barras finais
  if (/\/api$/i.test(x)) return x // já termina com /api
  return `${x}/api` // garante /api no fim
}

const RAW_BASE =
  (import.meta as any)?.env?.VITE_API_BASE_FORCE ??
  (import.meta as any)?.env?.VITE_API_BASE ??
  (import.meta as any)?.env?.VITE_API_BASE_URL ??
  (typeof __API_BASE__ !== 'undefined' ? __API_BASE__ : '') ??
  ''

export const API_BASE: string = normalizeBaseUrl(RAW_BASE)

// -----------------------------------------------------------------------------
// Util — detectar URL absoluta (http/https)
// -----------------------------------------------------------------------------
function isAbsoluteUrl(url?: string): boolean {
  if (!url) return false
  return /^https?:\/\//i.test(url)
}

// -----------------------------------------------------------------------------
// Instância Axios única
// -----------------------------------------------------------------------------
const api = axios.create({
  baseURL: API_BASE,
  withCredentials: false, // só JWT por header (sem cookies de sessão)
  timeout: 15000,
  validateStatus: (s) => s >= 200 && s < 300, // coerente com DRF
  // Serializer que remove null/undefined e serializa arrays como a=1&a=2
  paramsSerializer: (params) => {
    const usp = new URLSearchParams()
    if (!params) return usp.toString()

    const push = (k: string, v: unknown) => {
      if (v === undefined || v === null) return
      if (Array.isArray(v)) {
        v.forEach((item) => push(k, item))
      } else {
        usp.append(k, String(v))
      }
    }

    Object.keys(params).forEach((k) => push(k, (params as any)[k]))
    return usp.toString()
  },
})

// -----------------------------------------------------------------------------
// Helpers de Token (compatível com variações de store/local/sessionStorage)
// -----------------------------------------------------------------------------
function sanitizeToken(t?: string | null): string {
  if (!t) return ''
  return String(t)
    .replace(/^"+|"+$/g, '')
    .trim() // remove aspas e espaços
}

const storages: Array<Storage | undefined> = [localStorage, sessionStorage]

function readFromStorages(keys: string[]): string {
  for (const st of storages) {
    if (!st) continue
    for (const k of keys) {
      const v = st.getItem(k)
      if (v) return sanitizeToken(v)
    }
  }
  return ''
}

const getAccess = (): string => {
  try {
    const s = (useAuthStore as any)?.getState?.()
    const inStore =
      s?.accessToken ?? s?.access ?? s?.token ?? s?.state?.accessToken ?? ''
    if (inStore) return sanitizeToken(inStore)
  } catch {}
  return readFromStorages([
    'movatic.access',
    'accessToken',
    'access',
    'auth.access',
    'token',
  ])
}

const getRefresh = (): string => {
  try {
    const s = (useAuthStore as any)?.getState?.()
    const inStore =
      s?.refreshToken ?? s?.refresh ?? s?.state?.refreshToken ?? ''
    if (inStore) return sanitizeToken(inStore)
  } catch {}
  return readFromStorages([
    'movatic.refresh',
    'refreshToken',
    'refresh',
    'auth.refresh',
  ])
}

export function setAuthTokens(access: string, refresh: string) {
  persistTokens(access, refresh)
}

function persistTokens(access: string, refresh: string) {
  // Tenta atualizar a store (várias assinaturas suportadas)
  const st = (useAuthStore as any)?.getState?.()
  try {
    st?.setTokens?.({ access, refresh })
  } catch {}
  try {
    st?.setTokens?.({ accessToken: access, refreshToken: refresh })
  } catch {}
  // E grava nos storages
  storages.forEach((s) => {
    try {
      s?.setItem('movatic.access', access)
      s?.setItem('movatic.refresh', refresh)
      s?.setItem('access', access)
      s?.setItem('refresh', refresh)
    } catch {}
  })
}

// -----------------------------------------------------------------------------
// Normalização de caminhos (evita /api duplicado, // e garante trailing slash)
// -----------------------------------------------------------------------------
export type PathLike = string | undefined | null

export function normalizePath(u?: PathLike): string {
  if (!u) return '/'
  // URLs absolutas não devem ser normalizadas
  if (isAbsoluteUrl(String(u))) return String(u)

  // separa query/hash para preservar ao garantir trailing slash
  let url = String(u).trim()
  let qs = ''
  const qm = url.indexOf('?')
  if (qm >= 0) {
    qs = url.slice(qm) // inclui '?...'
    url = url.slice(0, qm)
  }

  // garante barra inicial
  if (!url.startsWith('/')) url = '/' + url

  // remove prefixo /api/ se passado por engano
  url = url.replace(/^\/api\//i, '/')

  // colapsa múltiplas barras
  url = url.replace(/\/{2,}/g, '/')

  // garante trailing slash (importante p/ DRF)
  if (!url.endsWith('/')) url += '/'

  return url + qs
}

// -----------------------------------------------------------------------------
// Interceptor de REQUEST: Authorization + Accept + normalização de URL
// -----------------------------------------------------------------------------
api.interceptors.request.use((config: InternalAxiosRequestConfig) => {
  if (typeof config.url === 'string' && !isAbsoluteUrl(config.url)) {
    config.url = normalizePath(config.url)
  }
  // Axios 1.x tem tipos chatinhos; mantém simples:
  const headers = (config.headers ?? {}) as AxiosRequestHeaders &
    Record<string, any>

  // Sempre aceitar JSON (a API usa DRF)
  if (!headers['Accept']) headers['Accept'] = 'application/json'

  // Idioma (opcional)
  if (!headers['Accept-Language']) {
    const lang =
      (navigator.languages && navigator.languages[0]) ||
      (navigator as any).userLanguage ||
      navigator.language ||
      'pt-PT'
    headers['Accept-Language'] = lang
  }

  // Injeta Authorization automaticamente se existir token
  const token = getAccess()
  if (token) headers['Authorization'] = `Bearer ${token}`

  config.headers = headers as any
  return config
})

// -----------------------------------------------------------------------------
// Refresh inteligente de token
// -----------------------------------------------------------------------------
const REFRESH_PATHS = [
  '/auth/jwt/refresh/',
  '/auth/token/refresh/',
  '/token/refresh/',
]
let lastWorkingRefreshPath: string | null = null

function isAuthUrl(url?: string): boolean {
  if (!url) return false
  const u = url.toLowerCase()
  return (
    u.includes('/auth/token') ||
    u.includes('/auth/jwt') ||
    u.includes('/token/refresh') ||
    u.includes('/auth/login') ||
    u.includes('/auth/logout')
  )
}

async function tryRefresh(refresh: string): Promise<string> {
  const order = lastWorkingRefreshPath
    ? [
        lastWorkingRefreshPath,
        ...REFRESH_PATHS.filter((p) => p !== lastWorkingRefreshPath),
      ]
    : [...REFRESH_PATHS]

  for (const path of order) {
    try {
      // Usa axios "cru" para não passar pelo interceptor e evitar laços
      const url = API_BASE + normalizePath(path)
      const resp = await axios.post(
        url,
        { refresh },
        { withCredentials: false },
      )
      const access =
        resp?.data?.access ??
        resp?.data?.access_token ??
        resp?.data?.token ??
        null
      if (access) {
        lastWorkingRefreshPath = path
        return sanitizeToken(access)
      }
    } catch {
      // tenta próximo endpoint
    }
  }
  throw new Error('refresh_failed')
}

// Controle de fila para refresh
let isRefreshing = false
let queue: Array<() => void> = []

// -----------------------------------------------------------------------------
// Interceptor de RESPONSE: tenta refresh em 401 e repete a requisição original
// -----------------------------------------------------------------------------
api.interceptors.response.use(
  (res: AxiosResponse) => res,
  async (error: AxiosError) => {
    const original = (error.config ?? {}) as InternalAxiosRequestConfig & {
      _retry?: boolean
    }
    const status = error.response?.status ?? 0

    // Não tenta refresh se a chamada original é de auth
    if (isAuthUrl(original?.url)) return Promise.reject(error)

    if (status === 401 && !original._retry) {
      original._retry = true

      const refresh = getRefresh()
      if (!refresh) {
        ;(useAuthStore as any)?.getState?.()?.logout?.()
        return Promise.reject(error)
      }

      if (isRefreshing) {
        await new Promise<void>((resolve) => queue.push(resolve))
        return api(original)
        // ^ reexecuta após o refresh em andamento
      }

      try {
        isRefreshing = true
        const newAccess = await tryRefresh(refresh)
        persistTokens(newAccess, refresh)

        // Libera a fila e reexecuta a chamada original
        queue.forEach((fn) => fn())
        queue = []
        return api(original)
      } catch (e) {
        ;(useAuthStore as any)?.getState?.()?.logout?.()
        return Promise.reject(e)
      } finally {
        isRefreshing = false
      }
    }

    return Promise.reject(error)
  },
)

// -----------------------------------------------------------------------------
// Helpers (aceitam path indefinido para evitar TS2345 em call sites)
// -----------------------------------------------------------------------------
export function apiGet<T = unknown>(
  path?: PathLike,
  config?: AxiosRequestConfig,
) {
  return api.get<T>(normalizePath(path), config)
}
export function apiPost<T = unknown>(
  path?: PathLike,
  data?: any,
  config?: AxiosRequestConfig,
) {
  return api.post<T>(normalizePath(path), data, config)
}
export function apiPut<T = unknown>(
  path?: PathLike,
  data?: any,
  config?: AxiosRequestConfig,
) {
  return api.put<T>(normalizePath(path), data, config)
}
export function apiPatch<T = unknown>(
  path?: PathLike,
  data?: any,
  config?: AxiosRequestConfig,
) {
  return api.patch<T>(normalizePath(path), data, config)
}
export function apiDelete<T = unknown>(
  path?: PathLike,
  config?: AxiosRequestConfig,
) {
  return api.delete<T>(normalizePath(path), config)
}
export function apiOptions<T = unknown>(
  path?: PathLike,
  config?: AxiosRequestConfig,
) {
  return api.options<T>(normalizePath(path), config)
}

// Ping simples (útil em health-checks do app)
export async function apiPing(): Promise<boolean> {
  try {
    await apiGet('/ping/')
    return true
  } catch {
    return false
  }
}

/* ========================================================================== */
/* ================= Workforce • Equipamentos (tipos + endpoints) ============ */
/* ========================================================================== */

export type StatusKey =
  | 'available'
  | 'busy'
  | 'maintenance'
  | 'offline'
  | string

export interface EquipmentDTO {
  id: number
  company?: unknown
  site?: unknown
  warehouse?: unknown
  type?: unknown
  type_image?: string | null
  code: string
  description?: string | null
  serial_no?: string | null
  status: StatusKey
  is_active: boolean
  current_location?: unknown | null
  parking_spot?: unknown | null
}

export interface Paginated<T> {
  count: number
  next: string | null
  previous: string | null
  results: T[]
}

export interface EquipmentListParams {
  page?: number
  page_size?: number
  search?: string
  status?: string
}

export const WF_EQUIPMENT_LIST = '/workforce/equipment/' // preferencial
export const WF_EQUIPMENT_LIST_ALT = '/workforce/equipments/' // fallback
export const WF_EQUIPMENT_STATUS = '/workforce/equipment/status/'
export const WF_EQUIPMENT_STATUS_ALT = '/workforce/equipments/status/'

/** Aux: tenta um GET em múltiplos paths e retorna o primeiro que funcionar */
async function getFirstOk<T>(
  paths: string[],
  cfg?: AxiosRequestConfig,
): Promise<T> {
  for (const p of paths) {
    try {
      const resp = await apiGet<T>(p, cfg)
      return resp.data as T
    } catch {
      /* tenta próximo */
    }
  }
  throw new Error('all_paths_failed')
}

/** Lista paginada de equipamentos (com fallback de rota) */
export async function wfEquipmentList(
  params: EquipmentListParams = {},
  cfg?: AxiosRequestConfig,
): Promise<Paginated<EquipmentDTO>> {
  const config: AxiosRequestConfig = { ...(cfg ?? {}), params }
  try {
    const resp = await apiGet<Paginated<EquipmentDTO>>(
      WF_EQUIPMENT_LIST,
      config,
    )
    return resp.data
  } catch {
    const respAlt = await apiGet<Paginated<EquipmentDTO>>(
      WF_EQUIPMENT_LIST_ALT,
      config,
    )
    return respAlt.data
  }
}

/** Lista completa (varre todas as páginas) */
export async function wfEquipmentListAll(
  params: EquipmentListParams = {},
): Promise<EquipmentDTO[]> {
  const pageSize = Math.max(50, Number(params.page_size || 100))
  const first = await wfEquipmentList({
    ...params,
    page: 1,
    page_size: pageSize,
  })
  const merged: EquipmentDTO[] = [...(first?.results ?? [])]

  let next: string | null = first?.next ?? null
  while (next) {
    // Quando `next` vier absoluto (/api/...), chamamos api.get com URL direta
    const resp = await api.get<Paginated<EquipmentDTO>>(next)
    const data = resp.data
    merged.push(...(data?.results ?? []))
    next = data?.next ?? null
  }
  return merged
}

/** Contagem por status (tenta endpoint dedicado e faz fallback por agregação local) */
export async function wfEquipmentStatusCounts(
  params: EquipmentListParams = {},
): Promise<Record<string, number>> {
  // 1) tenta endpoint dedicado (duas variantes)
  try {
    const data = await getFirstOk<Record<string, number>>(
      [WF_EQUIPMENT_STATUS, WF_EQUIPMENT_STATUS_ALT],
      { params },
    )
    if (data && typeof data === 'object') return data
  } catch {
    // ignora, usa fallback
  }

  // 2) fallback: agrega por status listando tudo
  const all = await wfEquipmentListAll(params)
  const out: Record<string, number> = {}
  for (const it of all) {
    const k = String(it.status || 'available').toLowerCase()
    out[k] = (out[k] ?? 0) + 1
  }
  // Garante chaves principais existirem
  for (const k of ['available', 'busy', 'maintenance', 'offline']) {
    if (!(k in out)) out[k] = 0
  }
  return out
}

// -----------------------------------------------------------------------------
// Exports
// -----------------------------------------------------------------------------
export default api
export { api }
export type { AxiosRequestConfig }
